import { useEffect, useRef, useState } from 'react';
import { Menu, MenuItem, useMenu } from '../components/Menu';

/**
 * The header — brand, nav, date, account pill and its menu, ported from views/layout.ejs.
 *
 * The markup is the EJS markup: same classes (`brand`, `nav a.active`, `admin-link`, `um-item`), same ids
 * (`user-menu-btn`, `user-menu`, `nav-date`), so public/app.css styles it and the browser tests that look
 * for those hooks can run against either front end. What the EJS app did in PFMenu (toggle, outside click,
 * Escape, aria-expanded) is the component's job here — a menu that only opens on click and never closes on
 * an outside click is the classic half-port.
 */

export type Me = {
  id: number;
  email: string;
  role: string;
  theme: string;
  logo_animate?: number | boolean;
};

const NAV = [
  { href: '/', label: 'Home', match: (p: string) => p === '/' },
  { href: '/tax', label: 'Taxes', match: (p: string) => p.startsWith('/tax') },
  { href: '/dividends', label: 'Dividends', match: (p: string) => p.startsWith('/dividends') },
  { href: '/equity', label: 'Equity', match: (p: string) => p.startsWith('/equity') },
];

export type NavItem = { href: string; label: string; match: (p: string) => boolean };

/** The logo: the still lockup, or the eye that glances and blinks when the account has it turned on. */
export function Logo({ animate }: { animate?: boolean }) {
  const pupils = useRef<SVGGElement[]>([]);
  const lids = useRef<SVGRectElement[]>([]);

  useEffect(() => {
    if (!animate) return;
    // prefers-reduced-motion wins over the account setting: an animation someone asked the OS to stop is
    // not a decoration, and the EJS header guards it the same way.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    let alive = true;
    const timers: number[] = [];

    const glance = () => {
      const x = (Math.random() * 2 - 1) * 38;
      const y = (Math.random() * 2 - 1) * 35;
      pupils.current.forEach((p) => {
        if (p) p.style.transform = `translate(${x.toFixed(1)}%, ${y.toFixed(1)}%)`;
      });
      timers.push(window.setTimeout(glance, 3000 + Math.random() * 2000));
    };
    const blink = () => {
      lids.current.forEach((l) => {
        if (!l) return;
        l.classList.remove('pf-opening');
        l.style.transform = 'scaleY(1)';
      });
      timers.push(
        window.setTimeout(() => {
          if (!alive) return;
          lids.current.forEach((l) => {
            if (!l) return;
            l.classList.add('pf-opening');
            l.style.transform = 'scaleY(0)';
          });
          timers.push(window.setTimeout(blink, 4000 + Math.random() * 6000));
        }, 150),
      );
    };
    glance();
    timers.push(window.setTimeout(blink, 2000 + Math.random() * 4000));
    return () => {
      alive = false;
      timers.forEach(window.clearTimeout);
    };
  }, [animate]);

  if (!animate) {
    return (
      <span className="logo-lockup">
        <img src="/logo.png" alt="Portfoolio" />
        <span className="pf-period" />
      </span>
    );
  }
  return (
    <span className="logo-eyes">
      <img src="/logo-anim.png" alt="Portfoolio" />
      <svg viewBox="0 0 1696 215" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} aria-hidden="true">
        <g className="pf-eye-l">
          <path
            fill="white"
            fillRule="evenodd"
            d="M932,30 H1029 A29,29 0 0 1 1058,59 V156 A29,29 0 0 1 1029,185 H932 A29,29 0 0 1 903,156 V59 A29,29 0 0 1 932,30 Z M941,45 H1020 A23,23 0 0 1 1043,68 V147 A23,23 0 0 1 1020,170 H941 A23,23 0 0 1 918,147 V68 A23,23 0 0 1 941,45 Z"
          />
          <rect
            ref={(el) => {
              if (el) pupils.current[0] = el as unknown as SVGGElement;
            }}
            className="pf-pupil"
            x="960.5"
            y="87"
            width="40"
            height="41"
            rx="5"
          />
          <rect
            ref={(el) => {
              if (el) lids.current[0] = el;
            }}
            className="pf-lid"
            x="903"
            y="30"
            width="155"
            height="155"
            rx="29"
            ry="29"
            fill="white"
          />
        </g>
        <g className="pf-eye-r">
          <path
            fill="white"
            fillRule="evenodd"
            d="M1121,30 H1218 A29,29 0 0 1 1247,59 V156 A29,29 0 0 1 1218,185 H1121 A29,29 0 0 1 1092,156 V59 A29,29 0 0 1 1121,30 Z M1130,45 H1209 A23,23 0 0 1 1232,68 V147 A23,23 0 0 1 1209,170 H1130 A23,23 0 0 1 1107,147 V68 A23,23 0 0 1 1130,45 Z"
          />
          <rect
            ref={(el) => {
              if (el) pupils.current[1] = el as unknown as SVGGElement;
            }}
            className="pf-pupil"
            x="1149.5"
            y="87"
            width="40"
            height="41"
            rx="5"
          />
          <rect
            ref={(el) => {
              if (el) lids.current[1] = el;
            }}
            className="pf-lid"
            x="1092"
            y="30"
            width="155"
            height="155"
            rx="29"
            ry="29"
            fill="white"
          />
        </g>
      </svg>
      <span className="pf-period" />
    </span>
  );
}

function AccountMenu({ user }: { user: Me }) {
  // The trigger's aria contract and the menu's keyboard behaviour come from the component: ArrowDown opens
  // onto Settings, Escape closes and returns focus here, and the rows below are MenuItems so each one is a
  // real menuitem (a menu of buttons that announce themselves as buttons is a menu only by sight).
  const m = useMenu({ menu: 'user-menu', trigger: 'user-menu-btn' });

  return (
    <span style={{ position: 'relative' }}>
      <button
        {...m.triggerProps}
        ref={m.triggerRef}
        className="t-body"
        type="button"
        style={{
          background: 'var(--panel)',
          border: '1px solid var(--border)',
          borderRadius: 999,
          color: 'var(--text)',
          lineHeight: 1,
          cursor: 'pointer',
          padding: '4px 14px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span>{user.email}</span>
        <span style={{ lineHeight: 1, transform: 'translateY(1px)' }}>▾</span>
      </button>
      <Menu {...m.menuProps} align="right" minWidth={140}>
        <div style={{ padding: '4px 0' }}>
          <MenuItem href="/settings/profile" className="um-item" icon={<GearIcon />}>
            Settings
          </MenuItem>
          {/* No divider between the two rows: the account menu is two actions, and a rule through the middle of a
              two-row menu reads as two sections. The form's own padding is the separation.
              A real form post: spa-server answers /logout by destroying the session and redirecting to
              /login, which is what the EJS page does. role="none" because a form is not a menu row. */}
          <form method="post" action="/logout" role="none" style={{ paddingTop: 4 }}>
            <MenuItem type="submit" className="um-item danger" icon={<LogoutIcon />}>
              Log out
            </MenuItem>
          </form>
        </div>
      </Menu>
    </span>
  );
}

/** The two icons the account menu has always carried, lifted out of the JSX so the rows read as rows. */
function GearIcon() {
  return (
    <span className="um-icon">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    </span>
  );
}

function LogoutIcon() {
  return (
    <span className="um-icon">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
      </svg>
    </span>
  );
}

export function Header({ user, path, dev, extraNav = [] }: { user: Me; path: string; dev?: boolean; extraNav?: NavItem[] }) {
  // The date is the client's, formatted exactly as the EJS header's script formats it — the header must
  // read the same on both front ends, including on a machine in another timezone.
  const [date, setDate] = useState('');
  useEffect(() => {
    setDate(new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }));
  }, []);

  return (
    <header>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <a className="brand" href="/" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <Logo animate={!!user.logo_animate} />
        </a>
        {dev ? (
          <span className="env-badge" title="dev">
            DEV
          </span>
        ) : null}
      </span>
      <nav>
        {[...NAV, ...extraNav].map((n) => (
          <a key={n.href} href={n.href} className={n.match(path) ? 'active' : undefined}>
            {n.label}
          </a>
        ))}
        {user.role === 'admin' ? (
          <a href="/admin/overview" className={path.startsWith('/admin') ? 'admin-link active' : 'admin-link'} title="Admin only">
            [ Admin ]
          </a>
        ) : null}
      </nav>
      <span className="spacer" />
      <span className="t-dense" id="nav-date" style={{ color: 'var(--muted)', marginRight: 14 }}>
        {date}
      </span>
      <AccountMenu user={user} />
    </header>
  );
}
